import bigHoleImage from "../assets/Fallback/Big Hole/bigHole04.jpg";
import diamondPavilionImage from "../assets/Fallback/Diamond Pavilion Mall/diamond06.jpg";
import kamfersDamImage from "../assets/KamferDam.jpg";
import lyveCafeImage from "../assets/Fallback/Lyve Cafe/lyve02.jpg";
import magersfonteinImage from "../assets/Fallback/Magersfontein Battlefield/magersfonteinBattlefield05.jpg";
import mcgregorMuseumImage from "../assets/Fallback/Mogregor museum/mcgregor06.jpg";
import mineMuseumImage from "../assets/Fallback/Kimberley Mine Museum/mineMuseum06.jpg";
import mokalaParkImage from "../assets/mokala-national-park-entrance-sign-01-500x300.jpg";
import rooiPoortImage from "../assets/PL-RooiPoort.jpg";
import williamGalleryImage from "../assets/william.jpg";

const knownCoordinates = {
  "ambassador-lodge": { lat: -28.7258, lng: 24.7738 },
  "batsumi-guest-lodge": { lat: -28.7667, lng: 24.7361 },
  "diamond-pavilion-mall": { lat: -28.7665, lng: 24.7589 },
  "inter-city": { lat: -28.7387, lng: 24.7627 },
  "kamfers-dam": { lat: -28.7307, lng: 24.8432 },
  "kimberley-mine-museum": { lat: -28.7381, lng: 24.7586 },
  "lyve-cafe": { lat: -28.7396, lng: 24.7638 },
  "magersfontein-battlefield": { lat: -28.9771, lng: 24.6939 },
  "mcgregor-museum": { lat: -28.7445, lng: 24.7906 },
  "mogregor-museum": { lat: -28.7445, lng: 24.7906 },
  "mokala-national-park": { lat: -29.1739, lng: 24.3302 },
  "rooipoort-nature-reserve": { lat: -28.5618, lng: 24.2762 },
  "shama-guest-house": { lat: -28.7368, lng: 24.7609 },
  "the-big-hole": { lat: -28.7381, lng: 24.7586 },
  "william-humphreys-art-gallery": { lat: -28.7439, lng: 24.7668 },
};

const fallbackImages = {
  "diamond-pavilion-mall": diamondPavilionImage,
  "kamfers-dam": kamfersDamImage,
  "kimberley-big-hole": bigHoleImage,
  "kimberley-mine-museum": mineMuseumImage,
  "lyve-cafe": lyveCafeImage,
  "magersfontein-battlefield": magersfonteinImage,
  "mcgregor-museum": mcgregorMuseumImage,
  "mogregor-museum": mcgregorMuseumImage,
  "mokala-national-park": mokalaParkImage,
  "rooipoort-nature-reserve": rooiPoortImage,
  "the-big-hole": bigHoleImage,
  "visit-the-big-hole": bigHoleImage,
  "william-humphreys-art-gallery": williamGalleryImage,
};

const normalizeKey = (value = "") =>
  value
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const getItemTitle = (item) => item.title || item.name || "Saved stop";

const getFallbackImage = (item) => {
  const candidates = [
    item.slug,
    item.id,
    item.title,
    item.name,
    item.location,
  ].filter(Boolean);

  for (const candidate of candidates) {
    const fallbackImage = fallbackImages[normalizeKey(candidate)];
    if (fallbackImage) return fallbackImage;
  }

  return "";
};

const getPrintableImageSrc = (item) => {
  const image = getFallbackImage(item) || item.image;
  if (!image || typeof window === "undefined") return image || "";

  try {
    return new URL(image, window.location.origin).href;
  } catch (error) {
    return image;
  }
};

export const getItemCoordinates = (item) => {
  const candidates = [
    item.slug,
    item.id,
    item.title,
    item.name,
    item.location,
  ].filter(Boolean);

  for (const candidate of candidates) {
    const coordinate = knownCoordinates[normalizeKey(candidate)];
    if (coordinate) return coordinate;
  }

  return null;
};

export const getDistanceKm = (from, to) => {
  if (!from || !to) return null;

  const earthRadiusKm = 6371;
  const degreesToRadians = (degrees) => (degrees * Math.PI) / 180;
  const latDifference = degreesToRadians(to.lat - from.lat);
  const lngDifference = degreesToRadians(to.lng - from.lng);
  const startLat = degreesToRadians(from.lat);
  const endLat = degreesToRadians(to.lat);

  const haversine =
    Math.sin(latDifference / 2) ** 2 +
    Math.cos(startLat) * Math.cos(endLat) * Math.sin(lngDifference / 2) ** 2;

  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
};

export const getRouteSegments = (orderedItems) =>
  orderedItems.map((item, index) => {
    const previousItem = orderedItems[index - 1];
    const distance =
      previousItem &&
      getDistanceKm(getItemCoordinates(previousItem), getItemCoordinates(item));

    return {
      item,
      distanceKm: typeof distance === "number" ? distance : null,
    };
  });

const getReadableDistance = (distanceKm) => {
  if (typeof distanceKm !== "number") return "Distance estimate unavailable";
  if (distanceKm < 1) return `${Math.round(distanceKm * 1000)} m from previous stop`;
  return `${distanceKm.toFixed(1)} km from previous stop`;
};

export const createPrintableItinerary = ({ items, orderedItems }) => {
  const routeSegments = getRouteSegments(orderedItems);
  const generatedDate = new Date().toLocaleDateString("en-ZA", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Kim Tour Saved Trip Itinerary</title>
    <style>
      @page { size: A4; margin: 16mm; }
      * { box-sizing: border-box; }
      body {
        margin: 0;
        background: #eef3f8;
        color: #102033;
        font-family: Arial, sans-serif;
      }
      .page {
        max-width: 920px;
        margin: 0 auto;
        background: #ffffff;
        border: 1px solid #d8e2ec;
        border-radius: 24px;
        overflow: hidden;
      }
      .hero {
        padding: 34px;
        color: #ffffff;
        background: linear-gradient(135deg, #003f72, #14847a);
      }
      .eyebrow {
        margin: 0 0 10px;
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      }
      h1 {
        margin: 0;
        font-family: Georgia, serif;
        font-size: 38px;
        line-height: 1.05;
      }
      .hero p {
        max-width: 620px;
        margin: 12px 0 0;
        color: #dbeafe;
        line-height: 1.6;
      }
      .meta {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 12px;
        padding: 18px 34px;
        background: #f7fafc;
        border-bottom: 1px solid #e3ebf3;
      }
      .meta div {
        padding: 12px;
        border-radius: 16px;
        background: #ffffff;
        border: 1px solid #e3ebf3;
      }
      .meta strong {
        display: block;
        color: #003f72;
        font-size: 18px;
      }
      .meta span {
        display: block;
        margin-top: 4px;
        color: #64748b;
        font-size: 12px;
      }
      .section {
        padding: 28px 34px;
      }
      h2 {
        margin: 0 0 16px;
        color: #003f72;
        font-family: Georgia, serif;
        font-size: 24px;
      }
      .route {
        display: grid;
        gap: 12px;
      }
      .route-row {
        display: grid;
        grid-template-columns: 42px 1fr 170px;
        gap: 14px;
        align-items: center;
        padding: 12px;
        border: 1px solid #e3ebf3;
        border-radius: 18px;
        background: #fbfdff;
      }
      .number {
        width: 38px;
        height: 38px;
        border-radius: 14px;
        background: #003f72;
        color: #ffffff;
        display: grid;
        place-items: center;
        font-weight: 900;
      }
      .route-row strong,
      .stop-card strong {
        display: block;
        color: #102033;
        font-size: 16px;
      }
      .route-row small,
      .stop-card small {
        display: block;
        margin-top: 4px;
        color: #64748b;
        line-height: 1.4;
      }
      .distance {
        color: #0f766e;
        font-size: 12px;
        font-weight: 800;
        text-align: right;
      }
      .stops {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 16px;
      }
      .stop-card {
        border: 1px solid #e3ebf3;
        border-radius: 18px;
        overflow: hidden;
        break-inside: avoid;
        background: #ffffff;
      }
      .stop-card img,
      .image-placeholder {
        width: 100%;
        height: 160px;
        object-fit: cover;
        display: block;
        background: #e8f1f8;
      }
      .image-placeholder {
        display: grid;
        place-items: center;
        color: #003f72;
        font-weight: 900;
      }
      .stop-content {
        padding: 14px;
      }
      .stop-content p {
        margin: 8px 0 0;
        color: #53657b;
        line-height: 1.55;
        font-size: 13px;
      }
      .note {
        margin-top: 18px;
        padding: 12px 14px;
        border-radius: 16px;
        background: #edf7f6;
        color: #315d58;
        font-size: 12px;
        line-height: 1.55;
      }
      @media print {
        body { background: #ffffff; }
        .page { border: 0; border-radius: 0; }
      }
    </style>
  </head>
  <body>
    <main class="page">
      <section class="hero">
        <p class="eyebrow">Kim Tour Itinerary</p>
        <h1>Saved Trip Itinerary</h1>
        <p>Your Kimberley stops, suggested order, images, and estimated distances in one clean travel plan.</p>
      </section>
      <section class="meta">
        <div><strong>${items.length}</strong><span>Saved stops</span></div>
        <div><strong>${routeSegments.filter((segment) => segment.distanceKm !== null).length}</strong><span>Distance estimates</span></div>
        <div><strong>${escapeHtml(generatedDate)}</strong><span>Generated</span></div>
      </section>
      <section class="section">
        <h2>Suggested Route Order</h2>
        <div class="route">
          ${routeSegments
            .map(
              ({ item, distanceKm }, index) => `
                <div class="route-row">
                  <span class="number">${index + 1}</span>
                  <div>
                    <strong>${escapeHtml(getItemTitle(item))}</strong>
                    <small>${escapeHtml(item.category || "Saved Idea")}${item.location ? ` · ${escapeHtml(item.location)}` : ""}</small>
                  </div>
                  <div class="distance">${index === 0 ? "Starting point" : escapeHtml(getReadableDistance(distanceKm))}</div>
                </div>`
            )
            .join("")}
        </div>
        <p class="note">Distances are approximate straight-line estimates between known Kimberley area coordinates. Use Maps for live driving directions and traffic.</p>
      </section>
      <section class="section">
        <h2>Saved Stops</h2>
        <div class="stops">
          ${orderedItems
            .map(
              (item) => {
                const imageSrc = getPrintableImageSrc(item);

                return `
                <article class="stop-card">
                  ${
                    imageSrc
                      ? `<img src="${escapeHtml(imageSrc)}" alt="${escapeHtml(getItemTitle(item))}" />`
                      : `<div class="image-placeholder">Kim Tour</div>`
                  }
                  <div class="stop-content">
                    <strong>${escapeHtml(getItemTitle(item))}</strong>
                    <small>${escapeHtml(item.category || "Saved Idea")}${item.location ? ` · ${escapeHtml(item.location)}` : ""}</small>
                    <p>${escapeHtml(item.description || "Saved Kimberley trip idea.")}</p>
                  </div>
                </article>`;
              }
            )
            .join("")}
        </div>
      </section>
    </main>
    <script>
      window.addEventListener("load", () => {
        const images = Array.from(document.images);
        const imageTasks = images.map((image) => {
          if (image.complete) return Promise.resolve();
          return new Promise((resolve) => {
            image.addEventListener("load", resolve, { once: true });
            image.addEventListener("error", resolve, { once: true });
          });
        });

        Promise.all(imageTasks).then(() => {
          setTimeout(() => window.print(), 300);
        });
      });
    </script>
  </body>
</html>`;
};
